import { ProductionInstance } from "@/instance/axios";
import { theme } from "@/styles/theme";
import { Flex, Text } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import {
  ProductionPlansGet200ResponseRowsInnerWorksInner,
  WorksItemGet200ResponseRowsInner,
  WorksSlittingScrapDetailPost200ResponseInner,
} from "@sizlcorp/sizl-api-document/dist/models";
import { IconMinus, IconPlus } from "@tabler/icons-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { DefectTypeItem } from "../ui/DefectTypeSet";
import { useCheckboxContext } from "../workByManagement/context/CheckboxProvider";
import { SocketData } from "../workByManagement/context/WorkProvider";
import { useWorkActions } from "../workByManagement/hooks/useWorkActions";
import { useWorkState } from "../workByManagement/hooks/useWorkState";
import { WorkEquipmentCell } from "../workByManagement/WorkEquipmentCell";
import { WorkNumberInputCell } from "../workByManagement/WorkNumberInputCell";
import { Td, WorkTr } from "../workByManagement/WorkTableRow";

interface WorkOperationProps {
  rowData: WorksItemGet200ResponseRowsInner;
  socketData?: SocketData;
  socket?: any;
  defectTypes: DefectTypeItem[] | null;
  bgColor?: string;
  formReset: boolean;
  children?: (
    slittingData?: Record<number, WorksSlittingScrapDetailPost200ResponseInner>,
  ) => React.ReactNode;
  workIds?: number[];
  onSlittingDataLoaded?: (data: WorksSlittingScrapDetailPost200ResponseInner[]) => void;
}

// 작업지시 Row의 작업 상태별 색상 정의
const workStatusColors = {
  WAITING: "",
  WORKING: theme.colors?.green?.[0],
  PAUSED: theme.colors?.orange?.[0],
  DONE: theme.colors?.gray?.[4],
};

const defaultWorkStatusColor = theme.colors?.gray?.[4];

export const WorkSlittingTableRow = (params: WorkOperationProps) => {
  const { rowData, defectTypes, bgColor, formReset, children, workIds, onSlittingDataLoaded } =
    params;
  const state = useWorkState();
  const actions = useWorkActions();
  const { areAllChecked } = useCheckboxContext();
  const queryClient = useQueryClient();
  const [slittingData, setSlittingData] = useState<
    Record<number, WorksSlittingScrapDetailPost200ResponseInner>
  >({});
  const trackingStatus = (rowData as ProductionPlansGet200ResponseRowsInnerWorksInner)
    .trackingStatus;

  const [opened, { toggle, open }] = useDisclosure(false);

  const handleDefectChange = ({ key, value }: { key: string; value: string }) => {
    const workData = state.tempDefects.find(
      (defect) => defect.id === (rowData.id && rowData?.id.toString()),
    );
    const data = workData?.defect.find((defect) => defect.key === key);
    if (data) {
      data.value = value;
    } else {
      workData
        ? workData.defect.push({ key, value })
        : state.tempDefects.push({
            id: rowData.id ? rowData.id.toString() : undefined,
            defect: [{ key, value }],
          });
    }
    actions.setTempDefects(state.tempDefects);
  };

  const getWorkIdsArray = (workIds: WorkOperationProps["workIds"]) => {
    if (Array.isArray(workIds)) {
      return workIds.filter((id): id is number => id !== undefined);
    }
    if (workIds && typeof workIds === "object") {
      return Object.values(workIds).filter((id): id is number => !!id);
    }
    return [];
  };
  const workIdsArray = getWorkIdsArray(workIds);

  const {
    data: slittingQueryData,
    isLoading: isSlittingLoading,
    refetch: refetchSlitting,
    isFetching: isSlittingFetching,
  } = useQuery(
    ["workSlitting", workIdsArray],
    () =>
      ProductionInstance.worksSlittingScrapDetailPost({
        worksSlittingScrapDetailPostRequest: {
          workIds: workIdsArray,
        },
      }),
    {
      enabled: opened && workIdsArray.length > 0,
      retry: false,
      select: (data) =>
        data.data?.reduce(
          (acc, cur) => {
            if (cur.workId !== undefined) {
              acc[cur.workId] = cur;
            }
            return acc;
          },
          {} as Record<number, WorksSlittingScrapDetailPost200ResponseInner>,
        ),
      onSuccess: (data) => {
        setSlittingData(
          Object.values(data)?.reduce(
            (acc, cur) => {
              if (cur.workId !== undefined) {
                acc[cur.workId] = cur;
              }
              return acc;
            },
            {} as Record<number, WorksSlittingScrapDetailPost200ResponseInner>,
          ),
        );
        if (onSlittingDataLoaded) {
          onSlittingDataLoaded(Object.values(data));
        }
      },
    },
  );

  const handleToggle = () => {
    toggle();
    if (opened) {
      // 닫힐 때 체크 해제
      actions.setScrapWorkIds(undefined); // 또는 해당 row만 해제
    }
  };

  useEffect(() => {
    if (opened && !isSlittingFetching) {
      refetchSlitting();
    }
  }, [opened]);

  return (
    <>
      <WorkTr
        color={
          rowData.id && trackingStatus ? workStatusColors[trackingStatus] : defaultWorkStatusColor
        }
      >
        <Td minWidth={5} width={8}>
          <Text ta="center">
            {!opened && (
              <IconPlus
                onClick={handleToggle}
                style={{ verticalAlign: "middle", cursor: "pointer" }}
              />
            )}
            {opened && (
              <IconMinus
                onClick={handleToggle}
                style={{ verticalAlign: "middle", cursor: "pointer" }}
              />
            )}
          </Text>
        </Td>
        <Td minWidth={5} width={8}>
          <Text ta="right">{rowData.scheduleSeq}</Text>
        </Td>
        <Td minWidth={12} width={20}>
          <WorkEquipmentCell
            data={{ equipmentCode: rowData.equipmentCode, equipmentName: rowData.equipmentName }}
          />
        </Td>
        <Td minWidth={22} width={31}>
          <Flex direction="row" align="center" justify="space-between">
            <Text>
              {rowData?.itemName}({rowData?.itemCode})
            </Text>
            <Text>{rowData?.spec}</Text>
          </Flex>
        </Td>
        {defectTypes?.map((defectType: DefectTypeItem) => (
          <Td key={defectType.value} minWidth={8} width={14}>
            {rowData?.id ? (
              <WorkNumberInputCell
                data={{
                  key: defectType.value,
                  defaultValue:
                    (rowData?.summary?.defect as any)?.[defectType.value]?.defectQuantity ?? "0",
                  value:
                    (rowData?.summary?.defect as any)?.[defectType.value]?.defectQuantity ?? "",
                  onChange: handleDefectChange,
                  formReset: formReset ?? false,
                }}
              />
            ) : null}
          </Td>
        ))}
      </WorkTr>
      {opened && typeof children === "function" && children(slittingData)}
    </>
  );
};
